import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ photos, index, onClose, onNav }) {
  const touchX = useRef(null);

  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, onNav]);

  const photo = photos[index];

  return (
    <motion.div
      data-testid="lightbox-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] bg-[#0f2417]/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) onNav(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <button
        data-testid="lightbox-close"
        aria-label="Close gallery"
        onClick={onClose}
        className="absolute top-5 right-5 z-10 text-[#f7f2e7]/70 hover:text-[#f7f2e7] transition-colors duration-300"
      >
        <X className="w-7 h-7" strokeWidth={1.5} />
      </button>
      <button
        data-testid="lightbox-prev"
        aria-label="Previous photo"
        onClick={(e) => {
          e.stopPropagation();
          onNav(-1);
        }}
        className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 z-10 text-[#f7f2e7]/60 hover:text-[#f7f2e7] transition-colors duration-300"
      >
        <ChevronLeft className="w-9 h-9" strokeWidth={1.5} />
      </button>
      <motion.figure
        key={photo.src}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="max-w-5xl w-full px-12 sm:px-20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border border-[#f7f2e7]/25 p-1.5 bg-[#16301f]/40">
          <img
            data-testid="lightbox-image"
            src={photo.src}
            alt={photo.alt}
            className="w-full max-h-[74vh] object-contain"
          />
        </div>
        <figcaption className="flex items-center justify-between gap-4 mt-4">
          <p className="text-xs text-[#f7f2e7]/60 font-light">{photo.alt}</p>
          <p data-testid="lightbox-counter" className="text-xs tracking-[0.25em] text-[#f7f2e7]/60 shrink-0">
            {index + 1} / {photos.length}
          </p>
        </figcaption>
      </motion.figure>
      <button
        data-testid="lightbox-next"
        aria-label="Next photo"
        onClick={(e) => {
          e.stopPropagation();
          onNav(1);
        }}
        className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 z-10 text-[#f7f2e7]/60 hover:text-[#f7f2e7] transition-colors duration-300"
      >
        <ChevronRight className="w-9 h-9" strokeWidth={1.5} />
      </button>
    </motion.div>
  );
}
